import { Newsletter, Profile, Randoms, Render, ShareIcons } from 'Components';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FC } from 'react';
import moment from 'moment';
import { DiscussionEmbed } from 'disqus-react';
import Image from 'next/image';

import './Article.scss';

interface ArticleProps {
  article?: any;
}

export const Article: FC<ArticleProps> = ({ article }) => {
  const { asPath } = useRouter();

  return (
    <>
      <div data-page="article">
        <Head>
          <title>{article?.title + ` | The Platform`}</title>

          <meta property="og:site_name" content="The Platform" />
          <meta property="og:url" content={`https://platformonline.uk/${asPath}`} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={article?.title} />
          <meta property="og:description" content={article?.summary} />
          <meta property="og:image" itemProp="image" content={article?.featuredImage?.url || '/static/img/logo.png'} />
          <meta property="og:url" content={`https://platformonline.uk/${asPath}`} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content="@YourPlatformUK" />
        </Head>

        {article && (
          <div className="article">
            <div className="section section">
              <div className="container">
                <div className="article-hero">
                  <Image
                    src={article.featuredImage?.url || '/static/img/logo.png'}
                    className="hero-image"
                    layout="fill"
                  />
                  <div className="hero-overlay">
                    <div className="hero-title" dangerouslySetInnerHTML={{ __html: article.title }} />
                  </div>
                  <Link href={`/author/${article.author.slug}`}>
                    <a className="hero-author">{article.author.name}</a>
                  </Link>
                </div>

                <div className="row">
                  <div className="col-xl-9">
                    {!!article.tags.length && (
                      <div className="article-cats">
                        {article.tags.map((tag: any) => (
                          <Link href={`/tag/${tag.slug}`} key={tag.slug}>
                            <a className="article-cat">#{tag.slug}</a>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="col-xl-9">
                    <div className="article-share-bottom visible-mobile">
                      <div className="share-buttons">
                        <ShareIcons title={article.title} path={asPath} tags={article.tags} key={article.id} />
                      </div>
                    </div>

                    <div className="article-content">
                      <div className="article-date">
                        <div className="date-item">{moment(article.date).format('ddd Do MMM YYYY')}</div>

                        {article.categories.map(category => (
                          <Link href={`/category/${category.slug}`} key={category.slug} passHref>
                            <a dangerouslySetInnerHTML={{ __html: category.name }} className="date-item" />
                          </Link>
                        ))}
                      </div>

                      {article.summary && (
                        <div className="article-summary" dangerouslySetInnerHTML={{ __html: article.summary }} />
                      )}

                      <span className="blank" />

                      <div className="article-share" data-key="share">
                        <ShareIcons title={article.title} path={asPath} tags={article.tags} />
                      </div>

                      <div dangerouslySetInnerHTML={{ __html: article.content }} />

                      <Render components={article.components} />
                    </div>
                  </div>

                  <div className="col-xl-3">
                    <div className="gap" />

                    <Profile author={article.author} />

                    <div className="gap" />
                    <div className="small-label">More from us</div>
                    <Randoms maxArticles={2} tags={article.tags} key={article?.id} article={article} />
                  </div>
                </div>
              </div>
            </div>

            <div className="container">
              <div className="row">
                <div className="col-xl-9">
                  <DiscussionEmbed
                    shortname="platformuk"
                    config={{
                      url: `https://platformonline.uk/${asPath}`,
                      identifier: article.id,
                      title: article?.title,
                      language: 'en_UK', //e.g. for Traditional Chinese (Taiwan)
                    }}
                  />

                  <div className="article-share-bottom">
                    <div className="share-buttons">
                      <ShareIcons title={article.title} path={asPath} tags={article.tags} key={article?.id} />
                    </div>
                  </div>
                  {/*<NewsletterComponent />*/}
                  <div className="gap" />
                </div>
              </div>
            </div>

            <div className="section section-alt">
              <div className="container">
                <Newsletter title="Sign up for more articles!" />
              </div>
            </div>

            <div className="section section-alt section">
              <div className="container">
                <Randoms
                  maxArticles={3}
                  label={'You may have missed'}
                  tags={article.tags}
                  key={article?.id}
                  article={article}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
